// src/pages/AdminSubmissions.js
import React, { useEffect, useState } from 'react';
import axios, { PUBLIC_BASE } from '../utils/axiosInstance';
import ListCard from '../components/ListCard';

const toArray = (d) =>
  Array.isArray(d) ? d : (d?.suggestions || d?.results || d?.lists || d?.data || []);

const toImageUrl = (raw) => {
  if (!raw) return '';
  if (typeof raw === 'string' && /^https?:\/\//i.test(raw)) return raw;
  const s = String(raw);
  const path = s.startsWith('/') ? s : `/${s}`;
  return `${PUBLIC_BASE}${path}`;
};

const pick = (o, keys, fallback = undefined) => {
  for (const k of keys) {
    const v = o?.[k];
    if (v !== undefined && v !== null && v !== '') return v;
  }
  return fallback;
};

const normalizeRow = (s) => {
  const id = pick(s, ['_id', 'id', 'suggestionId', 'listId']);
  const title = pick(s, ['name', 'listName', 'title'], 'Untitled');
  const rawImg =
    pick(s, ['image', 'cover', 'imageUrl', 'imageURL', 'thumbnail', 'imagePath', 'img']) ||
    pick(s?.image || s?.cover, ['url', 'path']);
  const imageUrl = toImageUrl(rawImg);

  const createdByObj =
    (typeof s.createdBy === 'object' && s.createdBy) ||
    (typeof s.user === 'object' && s.user) ||
    null;

  const userFromObj =
    (createdByObj && (createdByObj.username || createdByObj.email || createdByObj.name)) || '';

  const user =
    userFromObj ||
    pick(s, ['username', 'submittedBy', 'owner', 'createdBy', 'user']) ||
    '';

  return {
    id: id || `${title}|${user || 'unknown'}`,
    _sourceId: id || null,
    title,
    imageUrl,
    subtitle: typeof user === 'string' && user ? (user.startsWith('@') ? user : `@${user}`) : 'pending',
  };
};

const AdminSubmissions = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoad] = useState(true);
  const [err, setErr] = useState('');

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        setErr('');
        setLoad(true);

        // ✅ Only fetch pending from the correct endpoint
        const r = await axios.get('/suggestions', { params: { status: 'pending' } });
        const rows = toArray(r.data);

        const normalized = rows.map(normalizeRow).filter((x) => x.title);
        const seen = new Set();
        const deduped = [];
        for (const it of normalized) {
          if (seen.has(it.id)) continue;
          seen.add(it.id);
          deduped.push(it);
        }
        if (alive) setItems(deduped);
      } catch (e) {
        if (alive) setErr(e?.message || 'Failed to load submissions');
      } finally {
        if (alive) setLoad(false);
      }
    })();
    return () => { alive = false; };
  }, []);

  return (
    <div className="scroll-shell">{/* ← use the scrollable wrappers to restore scrollbar */}
      <div className="scroll-body">
        <h2 style={{ marginBottom: 12 }}>User Submissions</h2>

        {err && <div style={{ color: '#b91c1c', marginBottom: 12 }}>{err}</div>}

        {loading ? (
          <p style={{ color: '#666' }}>Loading…</p>
        ) : (
          <div className="grid">
            {items.map((it) => (
              <ListCard
                key={it.id}
                id={it.id}
                title={it.title}
                name={it.title}
                listName={it.title}
                subtitle={it.subtitle}
                imageUrl={it.imageUrl}
                to={it._sourceId ? `/admin/submissions/${it._sourceId}` : `/admin/submissions`}
              />
            ))}
          </div>
        )}

        {!loading && items.length === 0 && !err && (
          <div style={{ textAlign: 'center', color: '#777', marginTop: 24 }}>
            No user submissions right now.
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminSubmissions;
